import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import {
  ActionIcon, Alert, Button, ScrollArea, Text,
} from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import i18n from '../../../../i18n';
import {
  getTableConfigControls,
  isTableColumnsConfigurable,
  getTablePreviewLayout,
  getTablePreviewSample,
  getLegacyTableAlias,
  previewCatalogColumns,
  defaultTableConfig,
  resolveTableConfig,
} from '../../../../shared/tables/tableDescriptors';
import { getTableColumnsData } from '../../../../shared/tables/tableColumns';
import { awaitsRowKindsRefresh } from '../../../../shared/tables/tableRowKinds';
import { zoneSplitTableName } from '../../../../shared/tables/zoneSplit';
import { classicTableStyleFromDesign } from '../../../../shared/theme/themeConfiguration';
import { findRowStyle, resetRowStyle, setRowStyle } from '../../../../shared/tables/tableRowStyle';
import {
  mergeColumns,
  moveColumn,
  moveColumnByLabel,
  setColumnWidth,
  toggleColumnHidden,
  cycleColumnAlign,
  toColumnOverrides,
} from '../../../../shared/tables/tableConfigColumns';
import TableConfigBody from '../../../../shared/tables/ui/TableConfigBody';
import {
  tableConfigMantineWidgets,
  useHtml5DragAdapter,
} from './tableConfigMantineWidgets';
import {
  getTableCatalogSnapshot,
  getTableCatalogSource,
  isTemplateTableCatalog,
} from '../../../utils/tableCatalogRegistry';
import { getTableSchema } from '../../../../requests/documentDatas';
import { clearTableRawCache, readTable } from '../../../utils/webTableRawCache';
import { foldCanvasLabels, materializeTable } from '../../../utils/webTableMaterialize';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { useThemeConfiguration } from '../../../context/ThemeConfigurationContext';
import { storedTableDesign, withTableStyleDefaults } from './tableStyles';
import TableRowStyleSection from './TableRowStyleSection';
import { fromPuckTableConfig, toPuckTableConfig } from '../../../utils/puckTableConfig';
import styles from './TableConfigScreen.module.css';

/**
 * « Configurer le tableau » — le même écran que dans le Proposal : même
 * disposition (composant partagé `src/shared/tables/ui/TableConfigBody`), même
 * panneau qui glisse depuis la droite, seuls les contrôles sont en Mantine.
 *
 * Deux natures de réglage, et c'est toute l'affaire :
 *
 * - les **axes donnée** (mode, variante, consolidation…) sont rendus
 *   génériquement depuis les `controls` du descripteur, jamais en dur : un axe
 *   ajouté au registre apparaît ici tout seul, et part tout seul dans l'URL ;
 * - les **overrides de présentation** (masquer, renommer, réordonner,
 *   redimensionner) ne quittent jamais le navigateur.
 *
 * L'arbitrage « relire ou rejouer » n'est pas calculé : les axes donnée
 * entrent dans la clé de cache (`webTableRawCache`), pas la présentation. Une
 * colonne masquée retombe sur la réponse déjà lue, un changement de mode
 * provoque la lecture.
 */

const NOMINAL_WIDTH = 100;
const COMMIT_DELAY_MS = 300;
const PREVIEW_ROWS = 3;

/**
 * Colonnes d'une table de modèle, dans l'ordre du Drawer classique
 * (`fetchTableCatalog`) : sa forme côté API, sinon celle du tableau
 * paramétrable dont un ancien nom est l'alias, sinon ses colonnes d'exemple.
 * Un historique (Prix) n'a pas de forme sans lignes : sans ce repli, l'écran
 * restait vide. `null` : table illisible.
 */
async function templateTableCatalog(source, tableName, tableConfig, { split }) {
  const schema = await getTableSchema(source, tableName, { tableConfig });
  if (!schema) return null;
  if (schema.columns?.length) return schema.columns;
  const alias = getLegacyTableAlias(tableName);
  if (alias) {
    const aliasSchema = await getTableSchema(source, alias.tableName, {
      tableConfig: { ...tableConfig, ...alias.tableConfig },
    });
    return aliasSchema?.columns || null;
  }
  return previewCatalogColumns(tableName, tableConfig, { split });
}

export default function TableConfigScreen({ opened, onClose }) {
  const { itemProps, updateItemProps } = useSelectedItemProps();
  const tableName = itemProps.tableName || '';

  const [config, setConfig] = useState(
    () =>
      resolveTableConfig(tableName, fromPuckTableConfig(itemProps.tableConfig) || defaultTableConfig(tableName)) || { columns: [] }
  );
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const controls = getTableConfigControls(tableName);
  const columnsConfigurable = isTableColumnsConfigurable(tableName);
  const { capabilities } = getTableCatalogSnapshot();

  const configRef = useRef(config);
  const timerRef = useRef(null);
  const [refreshing, setRefreshing] = useState(false);

  // L'aperçu se rend avec le style du tableau du site, comme le Drawer avec
  // celui du document ; les polices par défaut sont celles du thème. Sans
  // `design` enregistré, le bloc s'affiche avec celui de ses défauts (Puck les
  // fusionne sous ses props au rendu) : l'aperçu aussi.
  const themeConfiguration = useThemeConfiguration();
  const design = storedTableDesign(itemProps.design);
  const previewStyle = useMemo(() => ({
    tableStyle: classicTableStyleFromDesign(withTableStyleDefaults(design), {}),
    configuration: themeConfiguration || {},
  }), [design, themeConfiguration]);

  /**
   * Clé des axes donnée : elle seule fait relire la table. La présentation en
   * est exclue — sinon masquer une colonne rechargerait le catalogue.
   */
  const axesKey = controls
    .map((c) => String(config[c.themeField || c.field] ?? ''))
    .join('|');

  /** Lit la table (cache ou réseau) et rematérialise le bloc. */
  const applyConfig = useCallback(
    async (nextConfig) => {
      const source = getTableCatalogSource();
      if (!source) return;
      // Modèle : aucune ligne à lire, la configuration attend la création du
      // site (le Proposal classique ne fait pas autre chose en modèle).
      if (isTemplateTableCatalog()) {
        updateItemProps({ tableConfig: toPuckTableConfig(nextConfig) });
        return;
      }
      try {
        const table = await readTable(source, tableName, nextConfig, {
          split: !!itemProps.shouldSplit,
        });
        const materialized = materializeTable(table, nextConfig, itemProps);
        // Un seul dispatch : configuration et contenu forment une seule
        // modification (`updateItemProps` repart des props du rendu courant).
        updateItemProps({ tableConfig: toPuckTableConfig(nextConfig), ...(materialized || {}) });
        setError(materialized ? null : 'Cette table ne renvoie aucune ligne.');
      } catch (e) {
        console.error('[webviewer] configuration du tableau', tableName, e);
        setError('Table illisible — le tableau garde son contenu actuel.');
      }
    },
    [tableName, itemProps, updateItemProps]
  );

  /** Écriture debouncée : une largeur se saisit chiffre par chiffre. */
  const commit = useCallback(
    (nextConfig, { immediate = false } = {}) => {
      configRef.current = nextConfig;
      setConfig(nextConfig);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      if (immediate) {
        applyConfig(nextConfig);
        return;
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        applyConfig(configRef.current);
      }, COMMIT_DELAY_MS);
    },
    [applyConfig]
  );

  /**
   * Fusion et style des lignes : lus au rendu du bloc (EditableDataTable), ils
   * ne changent pas les lignes — on écrit la config sans rematérialiser.
   */
  const writeRenderOnly = useCallback(
    (patch) => {
      const nextConfig = { ...configRef.current, ...patch };
      configRef.current = nextConfig;
      setConfig(nextConfig);
      updateItemProps({ tableConfig: toPuckTableConfig(nextConfig) });
    },
    [updateItemProps]
  );
  const changeMerge = (merge) => writeRenderOnly({ merge });

  // Ligne cliquée dans l'aperçu (`{ label, kind }`) et colonne de la case :
  // leur style se règle dans la sidebar, comme au panel de droite du classique.
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [styleScope, setStyleScope] = useState('row');
  const columnScope = styleScope === 'column' ? selectedColumn : null;
  const changeRowStyles = (rowStyles) => writeRenderOnly({ rowStyles });

  /** Relit la table au serveur (types de ligne compris) et rematérialise. */
  const refreshTable = async () => {
    setRefreshing(true);
    clearTableRawCache();
    try {
      await applyConfig(configRef.current);
    } finally {
      setRefreshing(false);
    }
  };

  /** Applique tout de suite ce qui attend — à la fermeture, sinon c'est perdu. */
  const flush = useCallback(() => {
    if (!timerRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    applyConfig(configRef.current);
  }, [applyConfig]);

  useEffect(() => () => timerRef.current && clearTimeout(timerRef.current), []);

  // Catalogue des colonnes. Rechargé quand un axe donnée bouge — une autre
  // variante n'expose pas les mêmes colonnes.
  //
  // Modèle : pas de lignes, on demande la forme de la table (`?catalog=true`),
  // comme le Drawer du Proposal classique (`fetchTableSchema`). Site : la
  // réponse de la table porte déjà le catalogue — et une offre ne sert pas de
  // schéma.
  useEffect(() => {
    if (!opened || !tableName) return undefined;
    let cancelled = false;
    setLoading(true);
    const source = itemProps.tableSource || getTableCatalogSource();
    if (isTemplateTableCatalog()) {
      templateTableCatalog(source, tableName, configRef.current, { split: !!itemProps.shouldSplit })
        .then((columns) => {
          if (cancelled) return;
          setCatalog(columns || []);
          setError(columns ? null : 'Table illisible — impossible de lister ses colonnes.');
        })
        .finally(() => !cancelled && setLoading(false));
      return () => {
        cancelled = true;
      };
    }
    readTable(source, tableName, configRef.current)
      .then((table) => {
        if (cancelled) return;
        const rows =
          table &&
          (table.rows ||
            Object.keys(table)
              .filter((k) => k.startsWith('rows'))
              .map((k) => table[k])[0]);
        if (!rows || !rows.length) {
          setCatalog([]);
          setError('Cette table ne renvoie aucune ligne.');
          return;
        }
        const { columns } = getTableColumnsData(
          { columns_informations: table.columns_informations, rows },
          NOMINAL_WIDTH
        );
        setCatalog(
          columns.map((c) => ({
            label: c.title,
            width: Number(c.width.toFixed(2)),
            ...(c.type === 'image' ? { type: 'image' } : {}),
          }))
        );
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        console.error('[webviewer] catalogue des colonnes', tableName, e);
        setError('Table illisible — impossible de lister ses colonnes.');
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [opened, tableName, axesKey, itemProps.shouldSplit]);

  const columns = useMemo(
    () => mergeColumns(catalog, config.columns),
    [catalog, config.columns]
  );

  const commitColumns = (next, options) =>
    commit({ ...configRef.current, columns: toColumnOverrides(next) }, options);

  const setAxis = (field, value) =>
    commit({ ...configRef.current, [field]: value }, { immediate: true });

  const moveByDrag = useCallback(
    (draggedLabel, overLabel) => {
      const current = mergeColumns(catalog, configRef.current.columns);
      const next = moveColumnByLabel(current, draggedLabel, overLabel);
      if (next === current) return;
      commit(
        { ...configRef.current, columns: toColumnOverrides(next) },
        { immediate: true }
      );
    },
    [catalog, commit]
  );

  const dragAdapter = useHtml5DragAdapter(moveByDrag);

  // Lignes d'exemple de la variante ; découpée par zone, la table lit sa
  // version « par zones » (titre de zone), comme au Drawer.
  const previewLayout = getTablePreviewLayout(
    itemProps.shouldSplit ? zoneSplitTableName(tableName) : tableName,
    config,
  );
  const blockRows = itemProps.shouldSplit
    ? (itemProps.splitSlices || []).flatMap((slice) => slice.rows || [])
    : itemProps.rows;
  const staleRowKinds = !isTemplateTableCatalog() && awaitsRowKindsRefresh(config, blockRows);

  const close = () => {
    flush();
    onClose();
  };

  // Le panneau se cale **sur le panneau latéral** : mêmes haut et bas, collé à
  // son bord gauche. Il ne recouvre donc ni l'en-tête ni la barre d'outils, et
  // c'est son propre contenu qui défile.
  //
  // La sidebar n'a pas de classe à elle : on part d'une ancre rendue sur place
  // (le panneau, lui, est porté dans le body — rendu dans la sidebar, il
  // héritait d'un bloc conteneur qui le tronquait par le haut) et on remonte
  // jusqu'à la colonne de la grille Puck.
  const anchorRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const inner = anchorRef.current?.closest('[class*="PuckLayout-inner"]');
      let column = anchorRef.current;
      while (column && column.parentElement && column.parentElement !== inner) {
        column = column.parentElement;
      }
      // Hors éditeur Puck (tests, montage isolé) : le panneau prend toute la
      // hauteur à droite plutôt que de ne pas s'afficher du tout.
      if (!inner || !column) {
        setAnchor({ top: 0, height: window.innerHeight, right: 0 });
        return;
      }
      const rect = column.getBoundingClientRect();
      setAnchor({
        top: Math.round(rect.top),
        height: Math.round(rect.height),
        right: Math.round(window.innerWidth - rect.left),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <>
      <span ref={anchorRef} className={styles.anchor} aria-hidden='true' />
      {/* Sans ligne-titre ni total, l'aperçu n'est pas cliquable : rien à styler. */}
      {opened && previewLayout && (
        <TableRowStyleSection
          row={selectedRow}
          rule={findRowStyle(config.rowStyles, selectedRow)}
          column={selectedColumn}
          scope={styleScope}
          onScopeChange={setStyleScope}
          tableFont={{
            family: previewStyle.tableStyle.fontFamilyCells || previewStyle.configuration.fontFamily,
            size: previewStyle.tableStyle.fontSizeCells || previewStyle.configuration.fontSize,
          }}
          onChange={(patch) => changeRowStyles(
            setRowStyle(configRef.current.rowStyles, selectedRow, patch, columnScope),
          )}
          onReset={() => changeRowStyles(
            resetRowStyle(configRef.current.rowStyles, selectedRow, columnScope),
          )}
        />
      )}
      {anchor &&
        createPortal(
          <div
            className={styles.clip}
            style={{
              top: anchor.top,
              right: anchor.right,
              height: anchor.height,
            }}
          >
            <div
          className={styles.panel}
          data-open={opened || undefined}
          aria-hidden={!opened || undefined}
        >
              <div className={styles.header}>
                <span className={styles.title}>
                  {i18n.t('table-configure')}
                </span>
                <ActionIcon
                  variant='subtle'
                  color='gray'
                  onClick={close}
                  aria-label='Fermer'
                >
                  <FontAwesomeIcon
                    icon={icon({ name: 'chevron-right', style: 'solid' })}
                    style={{ fontSize: 18 }}
                  />
                </ActionIcon>
              </div>
              <ScrollArea className={styles.scroll}>
                <div className={styles.content}>
                  <TableConfigBody
                    t={(key) => i18n.t(key)}
                    controls={controls}
                    config={config}
                    capabilities={capabilities}
                    columns={columns}
                    // Pas de colonnes à régler sans données (table vide), comme au Drawer.
                    columnsConfigurable={columnsConfigurable && (loading || catalog.length > 0)}
                    columnsUnavailableNote={!loading && catalog.length === 0 ? i18n.t('table-columns-unavailable') : null}
                    previewLayout={previewLayout}
                    previewSample={getTablePreviewSample(tableName, config)}
                    previewStyle={previewStyle}
                    mergeOptions={config.merge}
                    previewNotice={staleRowKinds && (
                      <>
                        <span>{i18n.t('table-merge-refresh-hint')}</span>
                        <Button size='compact-xs' color='editor' loading={refreshing} onClick={refreshTable}>
                          {i18n.t('table-merge-refresh')}
                        </Button>
                      </>
                    )}
                    // La fusion automatique ne se règle plus ici, comme au Drawer ;
                    // les exceptions déjà choisies restent listées.
                    showMergeSwitches={false}
                    onMergeChange={changeMerge}
                    onSelectedRowChange={(row, column) => {
                      setSelectedRow(row);
                      setSelectedColumn(column ?? null);
                    }}
                    loading={loading}
                    widgets={tableConfigMantineWidgets}
                    dragAdapter={dragAdapter}
                    previewRows={PREVIEW_ROWS}
                    banner={
                      error && (
                        <Alert
                          className={styles.error}
                          variant='light'
                          color='orange'
                          icon={<FontAwesomeIcon icon={icon({ name: 'circle-exclamation', style: 'light' })} style={{ fontSize: 16 }} />}
                          p='xs'
                        >
                          <Text size='xs'>{error}</Text>
                        </Alert>
                      )
                    }
                    onAxisChange={setAxis}
                    onToggleHidden={(label) =>
                      commitColumns(toggleColumnHidden(columns, label), {
                        immediate: true,
                      })
                    }
                    onRename={(label, value) =>
                      commitColumns(
                        columns.map((c) =>
                          c.label === label
                            ? {
                                ...c,
                                customLabel: value.length ? value : undefined,
                              }
                            : c
                        )
                      )
                    }
                    onResetName={(label) =>
                      commitColumns(
                        columns.map((c) =>
                          c.label === label ? { ...c, customLabel: undefined } : c
                        ),
                        { immediate: true }
                      )
                    }
                    onResetAllNames={() =>
                      commitColumns(
                        columns.map((c) => ({ ...c, customLabel: undefined })),
                        { immediate: true }
                      )
                    }
                    onWidth={(label, value) =>
                      commitColumns(setColumnWidth(columns, label, value))
                    }
                    onAlign={(label) =>
                      commitColumns(cycleColumnAlign(columns, label), { immediate: true })
                    }
                    onMove={(index, direction) => {
                      const next = moveColumn(columns, index, direction);
                      if (next !== columns)
                        commitColumns(next, { immediate: true });
                    }}
                  />
                </div>
              </ScrollArea>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
